import { Skeleton, tableHelpers, Tooltip } from '@round/creatorbase-ui';
import { influencer } from '@round/api';
import { asMoneyWithoutZeros } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { getMeta, CellContext, ExplorePlatform } from '../../helpers';
import {
    formatNumber,
    formatPercentage,
    getPostingFrequencyLabel,
    getPostingFrequencyDayGapLabel,
} from '../../../../utils/creatorSearchUtils';
import styles from './PlatformStatCells.module.css';

/** Shared by the cells and the expanded row's details panel, so a stat reads the same in both. */
export type PlatformValueGetter = (
    row: influencer.CreatorSearchResult,
    platform: ExplorePlatform,
    getCurrencyById: ReturnType<typeof useCurrencies>['getCurrencyById']
) => React.ReactNode;

// Platform stat cell factory that helps us DRY using the selected platform from table meta for different stat values.
const createPlatformStatCell = (getValue: PlatformValueGetter) => {
    return function PlatformStatCell({ row: { original }, table }: CellContext) {
        const { isLoading, getRowSelectedPlatform } = getMeta(table);
        const { getCurrencyById } = useCurrencies();

        const isBaseLoading = tableHelpers.isLoadingRow(original) || isLoading;

        if (isBaseLoading) {
            return <Skeleton className={styles.skeleton} />;
        }

        return getValue(original, getRowSelectedPlatform(original), getCurrencyById);
    };
};

export const getAvgCpeValue: PlatformValueGetter = (row, platform, getCurrencyById) =>
    asMoneyWithoutZeros(
        row.platforms?.[platform]?.[0]?.estimated_cpe,
        getCurrencyById?.(row.platforms?.[platform]?.[0]?.currency_id)
    );

export const AvgCpeCell = createPlatformStatCell(getAvgCpeValue);

export const getFollowersValue: PlatformValueGetter = (row, platform) =>
    formatNumber(row.metrics?.[platform]?.total_follower_count);

export const FollowersCell = createPlatformStatCell(getFollowersValue);

export const getCostValue: PlatformValueGetter = (row, platform, getCurrencyById) =>
    asMoneyWithoutZeros(
        row.platforms?.[platform]?.[0]?.cost,
        getCurrencyById?.(row.platforms?.[platform]?.[0]?.currency_id)
    );

export const CostCell = createPlatformStatCell(getCostValue);

export const getPlaysValue: PlatformValueGetter = (row, platform) =>
    formatNumber(row.metrics?.[platform]?.avg_video_views_per_post);

export const PlaysCell = createPlatformStatCell(getPlaysValue);

export const getCpmValue: PlatformValueGetter = (row, platform, getCurrencyById) =>
    asMoneyWithoutZeros(
        row.platforms?.[platform]?.[0]?.estimated_cpm,
        getCurrencyById?.(row.platforms?.[platform]?.[0]?.currency_id)
    );

export const CpmCell = createPlatformStatCell(getCpmValue);

export const getEngagementRateValue: PlatformValueGetter = (row, platform) =>
    formatPercentage(row.metrics?.[platform]?.avg_engagement_rate);

export const EnagementRateCell = createPlatformStatCell(getEngagementRateValue);

export const getPostingFrequencyValue: PlatformValueGetter = (row, platform) => {
    const avgPostsPerDay = row.metrics?.[platform]?.avg_posts_per_day;
    const label = getPostingFrequencyLabel(avgPostsPerDay);
    const dayGapLabel = getPostingFrequencyDayGapLabel(avgPostsPerDay);

    if (!dayGapLabel) {
        return label;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <span>{label}</span>
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom">{dayGapLabel}</Tooltip.Content>
        </Tooltip>
    );
};

export const PostingFrequencyCell = createPlatformStatCell(getPostingFrequencyValue);
